import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { formatLegalDate } from '../../utils/legalDates';
import './LegalReviewContent.css';

const buildSections = (content = '') =>
  content.split(/\n(?=\d+\.)/).map((section) => {
    const lines = section.trim().split('\n');
    return {
      title: lines[0],
      body: lines.slice(1).join('\n'),
    };
  });

const LegalReviewContent = ({
  showPrivacyToggle = true,
  onStateChange = null,
}) => {
  const [terms, setTerms] = useState(null);
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [termsError, setTermsError] = useState('');
  const [privacyError, setPrivacyError] = useState('');
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [loadingPrivacy, setLoadingPrivacy] = useState(false);
  const [activeDocument, setActiveDocument] = useState('terms');

  useEffect(() => {
    if (!terms && !loadingTerms && !termsError) {
      void fetchTerms();
    }

    if (showPrivacyToggle && !privacyPolicy && !loadingPrivacy && !privacyError) {
      void fetchPrivacyPolicy();
    }
  }, [showPrivacyToggle, terms, privacyPolicy, loadingTerms, loadingPrivacy, termsError, privacyError]);

  const acceptState = useMemo(() => ({
    canAccept: Boolean(terms) && !loadingTerms,
    loading: loadingTerms || (showPrivacyToggle && loadingPrivacy),
    termsId: terms?.id || null,
  }), [terms, loadingTerms, showPrivacyToggle, loadingPrivacy]);

  useEffect(() => {
    if (onStateChange) {
      onStateChange(acceptState);
    }
  }, [acceptState, onStateChange]);

  const fetchTerms = async () => {
    setLoadingTerms(true);
    setTermsError('');

    try {
      const response = await axios.get('/api/terms');
      const data = response.data;

      if (data.success) {
        setTerms(data.data.terms);
      } else {
        setTerms(null);
        setTermsError('Failed to load terms of use.');
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
      setTerms(null);
      setTermsError('Failed to load terms of use. Please try again.');
    } finally {
      setLoadingTerms(false);
    }
  };

  const fetchPrivacyPolicy = async () => {
    setLoadingPrivacy(true);
    setPrivacyError('');

    try {
      const response = await axios.get('/api/privacy');
      const data = response.data;

      if (data.success) {
        setPrivacyPolicy(data.data.privacy_policy);
      } else {
        setPrivacyPolicy(null);
        setPrivacyError('Failed to load privacy policy.');
      }
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
      setPrivacyPolicy(null);
      setPrivacyError('Failed to load privacy policy. Please try again.');
    } finally {
      setLoadingPrivacy(false);
    }
  };

  const isShowingPrivacy = showPrivacyToggle && activeDocument === 'privacy';
  const documentTitle = isShowingPrivacy
    ? (privacyPolicy ? privacyPolicy.title : 'Privacy Policy')
    : (terms ? terms.title : 'Terms of Use');
  const documentError = isShowingPrivacy ? privacyError : termsError;
  const isLoadingDocument = isShowingPrivacy ? loadingPrivacy : loadingTerms;
  const activeDocumentData = isShowingPrivacy ? privacyPolicy : terms;

  return (
    <div className="legal-review">
      {showPrivacyToggle && (
        <div className="legal-review__toggle-group" role="tablist" aria-label="Legal documents">
          <Button
            variant={activeDocument === 'terms' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setActiveDocument('terms')}
            disabled={loadingTerms}
          >
            Terms of Use
          </Button>
          <Button
            variant={activeDocument === 'privacy' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => setActiveDocument('privacy')}
            disabled={loadingPrivacy}
          >
            Privacy Policy
          </Button>
        </div>
      )}

      <div className="legal-review__document">
        <div className="legal-review__document-header">
          <h3>{documentTitle}</h3>
        </div>

        {isLoadingDocument ? (
          <div className="legal-review__loading">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading legal document...</span>
            </Spinner>
            <p>{isShowingPrivacy ? 'Loading privacy policy...' : 'Loading terms of use...'}</p>
          </div>
        ) : documentError ? (
          <Alert variant="danger">
            <div>{documentError}</div>
            <Button
              variant="outline-danger"
              size="sm"
              className="mt-3"
              onClick={isShowingPrivacy ? fetchPrivacyPolicy : fetchTerms}
            >
              Retry
            </Button>
          </Alert>
        ) : activeDocumentData ? (
          <>
            <div className="legal-review__document-body">
              {buildSections(activeDocumentData.content).map((section) => (
                <div key={section.title} className="legal-review__section">
                  <h4>{section.title}</h4>
                  <p>{section.body}</p>
                </div>
              ))}
            </div>

            <div className="legal-review__meta">
              <p>
                <strong>Effective Date:</strong> {formatLegalDate(activeDocumentData.effective_date)}
              </p>
              <p>
                <strong>Version:</strong> {activeDocumentData.version}
              </p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default LegalReviewContent;
